import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { Form, Field } from 'react-final-form';
import { useDispatch, flashSuccess, flashError } from '@/shared/components/flash';
import { getAccountingSettings, updateAccountingSettings } from '@/service/accountingSettings';

const CONFIRM_TEXT = 'Setting the hold to 0 turns it off for every program. Continue?';
// Largest value the API's decimal(12,2) column holds.
const MAX_THRESHOLD = 9999999999.99;

const toFormValues = (row) => ({
  deposit_hold_approval_threshold:
    row.deposit_hold_approval_threshold === null || row.deposit_hold_approval_threshold === undefined
      ? ''
      : String(Number(row.deposit_hold_approval_threshold)),
  deposit_hold_business_hours: String(row.deposit_hold_business_hours),
});

const validate = (values) => {
  const errors = {};
  const threshold = values.deposit_hold_approval_threshold;
  if (threshold !== '' && threshold !== undefined && threshold !== null) {
    const text = String(threshold).trim();
    const n = Number(text);
    if (!Number.isNaN(n) && n <= 0) errors.deposit_hold_approval_threshold = 'Threshold must be more than 0. Leave it empty for no threshold.';
    // Plain digits only: "1,000", "$1000" or "1e3" must be rejected, never read as "no threshold".
    else if (!/^\d+(\.\d+)?$/.test(text)) errors.deposit_hold_approval_threshold = 'Enter an amount like 1250.00.';
    // The API stores dollars and cents (decimal 12,2); reject anything it would round or clamp.
    else if (!/^\d+(\.\d{1,2})?$/.test(text)) errors.deposit_hold_approval_threshold = 'Threshold can have at most 2 decimal places.';
    else if (n > MAX_THRESHOLD) errors.deposit_hold_approval_threshold = 'Threshold can be at most 9,999,999,999.99.';
  }
  const hours = values.deposit_hold_business_hours;
  const h = Number(hours);
  if (hours === '' || hours === undefined || hours === null || !Number.isInteger(h) || h < 0 || h > 720) {
    errors.deposit_hold_business_hours = 'Hours must be a whole number from 0 to 720.';
  }
  return errors;
};

const FieldError = ({ meta }) => {
  const message = ((meta.touched || meta.submitFailed) && meta.error) || (!meta.dirtySinceLastSubmit && meta.submitError);
  return message ? <span className="form__form-group-error text-danger">{message}</span> : null;
};

const AccountingSettingsForm = () => {
  const dispatch = useDispatch();
  const [saved, setSaved] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let active = true;
    getAccountingSettings().then((result) => {
      if (!active) return;
      if (result.data) setSaved(result.data);
      else if (result.status === 403) setLoadError('Super Admin access required.');
      else setLoadError(typeof result.errors === 'string' ? result.errors : 'Accounting settings could not be loaded.');
    });
    return () => {
      active = false;
    };
  }, []);

  if (loadError) return <p className="text-danger">{loadError}</p>;
  if (!saved) return <p>Loading...</p>;

  const onSubmit = async (values, form) => {
    const hours = Number(values.deposit_hold_business_hours);
    if (hours === 0 && saved.deposit_hold_business_hours > 0 && !window.confirm(CONFIRM_TEXT)) return undefined;

    const threshold = values.deposit_hold_approval_threshold;
    const result = await updateAccountingSettings({
      deposit_hold_business_hours: hours,
      deposit_hold_approval_threshold: threshold === '' || threshold === undefined ? null : Number(threshold),
    });

    if (result.data) {
      setSaved(result.data);
      flashSuccess(dispatch, 'Accounting settings saved');
      // Defer so final-form finishes its submit cycle before re-initialising.
      setTimeout(() => form.restart(toFormValues(result.data)));
      return undefined;
    }
    if (result.errors && typeof result.errors === 'object') return result.errors;
    flashError(dispatch, result.status === 403 ? 'Super Admin access required.' : result.errors);
    return undefined;
  };

  return (
    <Form onSubmit={onSubmit} validate={validate} initialValues={toFormValues(saved)}>
      {({ handleSubmit, pristine, submitting }) => (
        <form className="form form--horizontal accounting-settings-form" onSubmit={handleSubmit} noValidate>
          <div className="form__form-group">
            <label className="form__form-group-label" htmlFor="deposit_hold_approval_threshold">
              Super Admin approval threshold ($)
            </label>
            <div className="form__form-group-field flex-column align-items-stretch">
              <Field name="deposit_hold_approval_threshold">
                {({ input, meta }) => (
                  <>
                    {/* Text, not number: a number input reports unparseable text ("1,000") as empty, which would clear the threshold. */}
                    <input {...input} id="deposit_hold_approval_threshold" type="text" inputMode="decimal" autoComplete="off" />
                    <FieldError meta={meta} />
                  </>
                )}
              </Field>
              <small className="text-muted">
                Payments on an invoice whose total is at or above this amount need Super Admin approval before funds release, even
                after the hold has run. Leave empty for no threshold. Applies only to payments made after you save.
              </small>
            </div>
          </div>

          <div className="form__form-group">
            <label className="form__form-group-label" htmlFor="deposit_hold_business_hours">
              Deposit hold (business hours)
            </label>
            <div className="form__form-group-field flex-column align-items-stretch">
              <Field name="deposit_hold_business_hours">
                {({ input, meta }) => (
                  <>
                    <input {...input} id="deposit_hold_business_hours" type="number" min="0" max="720" step="1" required />
                    <FieldError meta={meta} />
                  </>
                )}
              </Field>
              <small className="text-muted">0 turns the hold off for every program.</small>
            </div>
          </div>

          <div className="accounting-settings-form__actions">
            <Button type="submit" size="sm" color="primary" className="mb-0" disabled={pristine || submitting}>
              Save
            </Button>
          </div>
          <p className="text-muted accounting-settings-form__note">Every change is recorded with who made it.</p>
        </form>
      )}
    </Form>
  );
};

export default AccountingSettingsForm;
